import { LuArrowRight } from 'react-icons/lu';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import authService from '../../services/api/auth.service';
import validator from 'validator';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import platform from 'platform';

const PhoneAuth = ({
    setLoginState,
    onPasswordLogin,
    phoneNumber,
    setPhoneNumber
}: {
    setLoginState: (state: 'PHONE' | 'PASSWORD' | 'OTP') => void,
    onPasswordLogin: () => void,
    phoneNumber: string,
    setPhoneNumber: (phone: string) => void
}) => {
    const isPhoneNumberValid = validator.isMobilePhone(phoneNumber, 'en-IN');
    const [phoneError, setPhoneError] = useState('');
    const [initialData, setInitialData] = useState<any>(null);
    const [searchParams] = useSearchParams();

    // Get ref, src, and medium from query parameters
    const ref = searchParams.get('ref') ?? '';
    const source = searchParams.get('src') ?? '';
    const medium = searchParams.get('medium') ?? '';
    const identifier = searchParams.get('init') ?? '';
    const click_id = searchParams.get('click_id') ?? '';

    // Get user device information
    const getUserDeviceInfo = () => {
        const userAgent = navigator.userAgent;
        const platform = navigator.platform;
        return `${platform} - ${userAgent}`;
    };

    const deviceInfo = {
        os: platform.os,            // e.g. {family: "OS X", version: "10.15.7"}
        browser: platform.name,     // e.g. "Chrome"
        browserVersion: platform.version, // e.g. "116.0.0"
        description: platform.description, // full info string
        product: platform.product   // e.g. "iPhone"
    };

    const onGetOTP = async () => {
        try {
            // Create payload with all available data
            const payload = {
                phone_no: phoneNumber,
                ref,
                source,
                medium,
                click_id,
                user_device: deviceInfo
            };

            const response = await authService.loginWithOTP(payload);
            toast.success(response.message);
            setLoginState('OTP');
        } catch (error) {
            toast.error((error as any).response.data.message || 'Something went wrong');
        }
    }

    const handlePhoneNumberChange = (value: string) => {
        const numericValue = value.replace(/\D/g, '');
        setPhoneNumber(numericValue);

        if (numericValue.length < 10) {
            setPhoneError('Phone number must be 10 digits');
        } else if (!validator.isMobilePhone(numericValue, 'en-IN')) {
            setPhoneError('Invalid phone number format');
        } else {
            setPhoneError('');
        }
    };

    const fetchInitialData = async () => {
        try {
            const response = await authService.initUser(identifier);
            setInitialData(response.data);
            setPhoneNumber(response.data.phone_no);
        } catch (error) {
            console.log((error as any).response.data.message || 'Something went wrong');
        }
    };

    useEffect(() => {
        if (!identifier) return;
        fetchInitialData();
    }, [identifier]);

    return (
        <div className="space-y-6">
            <p className='text-center font-semibold text-xl capitalize'>{initialData && initialData.first_name && `Hi, ${(initialData.first_name).toLowerCase()} 👋`}</p>
            <div className="relative">
                <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none border-r-2 pr-3 text-gray-500">
                    +91
                </div>
                <input
                    type="tel"
                    className="block w-full pl-16 pr-4 py-3.5 border-2 border-gray-200 rounded-lg focus:border-primary-700 focus:ring-primary-700 transition-colors bg-white/5"
                    placeholder="Enter phone number"
                    value={phoneNumber}
                    onChange={(e) => handlePhoneNumberChange(e.target.value)}
                    maxLength={10}
                />
            </div>
            {phoneError && <p className="text-red-500 text-sm">{phoneError}</p>}
            <div className="flex flex-col items-center justify-center gap-2">
                <motion.button
                    onClick={onGetOTP}
                    disabled={!isPhoneNumberValid}
                    className="py-3.5 px-4 w-full bg-primary-800 text-white rounded-lg hover:bg-primary-700 transition-all flex items-center justify-center gap-2 font-medium disabled:bg-white disabled:text-gray-400 border-2 disabled:cursor-not-allowed"
                >
                    Get OTP
                    <LuArrowRight className="w-4 h-4" />
                </motion.button>
            </div>
        </div>
    );
};

export default PhoneAuth;